import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useSchoolSearch, useClassSearch, useProfessorSearch, usePopularClasses,
  useCreateSchool, useCreateClass, useCreateProfessor, useCreateSection, useEnrollSection,
  useQuestionsFeed,
} from '../lib/api';
import { Combobox, type ComboboxItem } from '../components/Combobox';
import { motion, AnimatePresence, springs, Pressable, FadeIn, Confetti } from '../lib/motion';
import { haptic } from '../lib/haptics';
import type { School, ClassCode, Professor } from '@shared/types';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2020 + 2 }, (_, i) => 2020 + i).reverse();
const TERMS = ['Winter', 'Spring', 'Summer', 'Fall'];

const slideVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 44 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -44 }),
};

export function OnboardingPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  const [school, setSchool] = useState<School | null>(null);
  const [classCode, setClassCode] = useState<ClassCode | null>(null);
  const [professor, setProfessor] = useState<Professor | null>(null);
  const [allTeachers, setAllTeachers] = useState(false);
  const [year, setYear] = useState<number | null>(null);
  const [term, setTerm] = useState<string | null>(null);
  const [sectionId, setSectionId] = useState<string | null>(null);

  const createSection = useCreateSection();
  const enroll = useEnrollSection();

  const skipProfessor = /\bnational\s+university\b/i.test(school?.name ?? '');
  const totalSteps = skipProfessor ? 5 : 6;

  const handleFinishSetup = async () => {
    if (!classCode || !year) return;
    const termStr = term ? `${term} ${year}` : `${year}`;
    const section = await createSection.mutateAsync({
      classCodeId: classCode.id,
      professorId: allTeachers ? null : (professor?.id ?? null),
      term: termStr,
      examDate: null,
    });
    await enroll.mutateAsync(section.section.id);
    setSectionId(section.section.id);
    setStep(step + 1);
  };

  const progressPercent = Math.round(((step - 1) / totalSteps) * 100);

  // Direction of travel for step transitions (visual only)
  const prevStepRef = useRef(step);
  const dir = step >= prevStepRef.current ? 1 : -1;
  useEffect(() => { prevStepRef.current = step; }, [step]);

  const isDone = step === 6;
  const displayStep = Math.min(skipProfessor && step > 2 ? step - 1 : step, totalSteps);
  const displayPercent = isDone ? 100 : progressPercent;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      {/* Progress header */}
      <div className="px-6 pt-4 pb-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
            {isDone ? 'All set' : `Step ${displayStep} of ${totalSteps}`}
          </span>
          <span className="text-[11px] font-bold text-squishy-tealDark tabular-nums">{displayPercent}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-squishy-tealDark to-squishy-teal"
            initial={false}
            animate={{ width: `${displayPercent}%` }}
            transition={springs.soft}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-6 pt-6 pb-32 overflow-y-auto sm:items-center sm:py-8">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.div
              key={step}
              custom={dir}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={springs.soft}
            >
              {step === 1 && (
                <SchoolStep
                  school={school}
                  onSelect={(s) => { setSchool(s); setStep(2); }}
                  onBack={() => nav('/')}
                />
              )}
              {step === 2 && school && (
                <ClassStep
                  school={school}
                  classCode={classCode}
                  onSelect={(c) => { setClassCode(c); setStep(skipProfessor ? 4 : 3); }}
                  onBack={() => setStep(1)}
                />
              )}
              {step === 3 && !skipProfessor && school && (
                <ProfessorStep
                  school={school}
                  onSelect={(p, all) => { setProfessor(p); setAllTeachers(all); setStep(4); }}
                  onBack={() => setStep(2)}
                />
              )}
              {step === 4 && (
                <YearStep
                  year={year}
                  onSelect={(y) => { setYear(y); setStep(5); }}
                  onBack={() => setStep(skipProfessor ? 2 : 3)}
                />
              )}
              {step === 5 && (
                <TermStep
                  term={term}
                  onSelect={(t) => { setTerm(t); handleFinishSetup(); }}
                  onSkip={() => { setTerm(null); handleFinishSetup(); }}
                  onBack={() => setStep(4)}
                  loading={createSection.isPending || enroll.isPending}
                />
              )}
              {step === 6 && sectionId && classCode && (
                <DoneStep
                  sectionId={sectionId}
                  classCode={classCode.code}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

// ── Shared step chrome (visual only) ────────────────────────────────────────

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Pressable
      onClick={onClick}
      className="inline-flex items-center gap-1 text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors min-h-[44px] -ml-1.5 px-1.5"
      aria-label="Go back"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
      </svg>
      Back
    </Pressable>
  );
}

function StepHeading({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div>
      <h1 className="font-display text-3xl text-squishy-navy leading-tight">{title}</h1>
      {children && <p className="text-slate-500 mt-2 text-[15px] leading-relaxed">{children}</p>}
    </div>
  );
}

// ── Step 1: School ──────────────────────────────────────────────────────────

function SchoolStep({ school, onSelect, onBack }: {
  school: School | null;
  onSelect: (s: School) => void;
  onBack: () => void;
}) {
  const [q, setQ] = useState(school?.name ?? '');
  const [showCityState, setShowCityState] = useState(false);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const search = useSchoolSearch(q);
  const create = useCreateSchool();

  const items: ComboboxItem[] = (search.data?.schools ?? []).map((s) => ({
    id: s.id,
    label: s.name,
    sublabel: [s.city, s.state].filter(Boolean).join(', ') || undefined,
  }));

  const handleCreate = async () => {
    if (!showCityState) { setShowCityState(true); return; }
    const r = await create.mutateAsync({ name: q.trim(), city: city.trim() || undefined, state: state.trim() || undefined });
    if (r.school) onSelect(r.school);
  };

  return (
    <div className="space-y-6">
      <BackButton onClick={onBack} />
      <StepHeading title="What school do you go to?">
        Start typing and we'll find it for you.
      </StepHeading>
      <Combobox
        placeholder="Search your school..."
        value={q}
        onChange={(v) => { setQ(v); setShowCityState(false); }}
        results={items}
        loading={search.isLoading}
        mobileInlineResults
        onSelect={(item) => {
          const found = search.data?.schools?.find((s) => s.id === item.id);
          onSelect(found ?? { id: item.id, name: item.label, city: null, state: null, country: 'US', domain: null, verified: false });
        }}
        onCreate={handleCreate}
        createLabel={showCityState ? undefined : 'Add school'}
      />
      <AnimatePresence>
        {showCityState && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={springs.soft}
            className="space-y-3 border border-slate-200 rounded-2xl p-4 bg-slate-50"
          >
            <p className="text-sm text-slate-600">Where is <strong>{q.trim()}</strong> located?</p>
            <input value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="City (e.g. San Diego)"
              className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-squishy-teal focus:shadow-glow transition-shadow" />
            <input value={state} onChange={(e) => setState(e.target.value)}
              placeholder="State (e.g. California)"
              className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-squishy-teal focus:shadow-glow transition-shadow" />
            <Pressable
              haptic="medium"
              onClick={handleCreate}
              disabled={create.isPending || !q.trim()}
              className="w-full bg-squishy-tealDark text-white font-semibold py-3.5 rounded-2xl text-base shadow-card disabled:opacity-50"
            >
              {create.isPending ? 'Adding...' : `Add "${q.trim()}"`}
            </Pressable>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Step 2: Class ───────────────────────────────────────────────────────────

function ClassStep({ school, classCode, onSelect, onBack }: {
  school: School;
  classCode: ClassCode | null;
  onSelect: (c: ClassCode) => void;
  onBack: () => void;
}) {
  const [q, setQ] = useState(classCode?.code ?? '');
  const search = useClassSearch(school.id, q);
  const popular = usePopularClasses(school.id);
  const create = useCreateClass();

  // Show popular classes when search is empty, search results otherwise
  const showPopular = q.length === 0 && (popular.data?.classes?.length ?? 0) > 0;
  const items: ComboboxItem[] = showPopular
    ? (popular.data?.classes ?? []).map((c) => ({
        id: c.id, label: c.code, sublabel: c.name ? `${c.name} (popular)` : 'Popular',
      }))
    : (search.data?.classes ?? []).map((c) => ({
        id: c.id, label: c.code, sublabel: c.name ?? undefined,
      }));

  return (
    <div className="space-y-6">
      <BackButton onClick={onBack} />
      <StepHeading title="What class are you taking?">
        Type the class code or name, like <span className="font-medium text-slate-700">NSG 250</span>, <span className="font-medium text-slate-700">PHYS 101</span>, or <span className="font-medium text-slate-700">Intro to Nursing</span>.
      </StepHeading>
      <Combobox
        placeholder="Search or add a class..."
        value={q}
        onChange={setQ}
        results={items}
        loading={search.isLoading}
        autoOpen
        emptyLabel="No classes found. Type to add one."
        mobileInlineResults
        onSelect={(item) => onSelect({ id: item.id, schoolId: school.id, code: item.label, name: (item.sublabel ?? '').replace(/ \(popular\)$/, '') || null })}
        onCreate={async () => {
          const r = await create.mutateAsync({ schoolId: school.id, code: q.trim().toUpperCase() });
          if (r.class) onSelect(r.class);
        }}
        createLabel="Add class"
      />
    </div>
  );
}

// ── Step 3: Professor ───────────────────────────────────────────────────────

function ProfessorStep({ school, onSelect, onBack }: {
  school: School;
  onSelect: (p: Professor | null, allTeachers: boolean) => void;
  onBack: () => void;
}) {
  const [q, setQ] = useState('');
  const search = useProfessorSearch(school.id, q);
  const create = useCreateProfessor();

  const items: ComboboxItem[] = (search.data?.professors ?? []).map((p) => ({
    id: p.id, label: p.name,
  }));

  return (
    <div className="space-y-6">
      <BackButton onClick={onBack} />
      <StepHeading title="Who's your professor?">
        This is optional. Skip if you're not sure.
      </StepHeading>
      <Combobox
        placeholder="Search professor name..."
        value={q}
        onChange={setQ}
        results={items}
        loading={search.isLoading}
        mobileInlineResults
        onSelect={(item) => onSelect({ id: item.id, schoolId: school.id, name: item.label, verified: false }, false)}
        onCreate={async () => {
          const r = await create.mutateAsync({ schoolId: school.id, name: q.trim() });
          if (r.professor) onSelect(r.professor, false);
        }}
        createLabel="Add professor"
      />
      <Pressable
        onClick={() => onSelect(null, true)}
        className="w-full py-3.5 border-2 border-dashed border-slate-300 rounded-2xl text-sm text-slate-500 font-medium hover:border-slate-400 hover:text-slate-600 transition-colors"
      >
        Skip this step
      </Pressable>
    </div>
  );
}

// ── Step 4: Year ────────────────────────────────────────────────────────────

function YearStep({ year, onSelect, onBack }: {
  year: number | null;
  onSelect: (y: number) => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-6">
      <BackButton onClick={onBack} />
      <StepHeading title="What year?">
        Select the year you're taking this class.
      </StepHeading>
      <div className="grid grid-cols-3 gap-3">
        {YEARS.map(y => (
          <Pressable
            key={y}
            haptic="selection"
            onClick={() => onSelect(y)}
            aria-pressed={year === y}
            className={`py-4 min-h-[56px] rounded-2xl text-lg font-semibold border-2 transition-colors ${
              year === y
                ? 'border-squishy-teal bg-teal-50 text-squishy-tealDark shadow-glow'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 shadow-card'
            }`}
          >
            {y}
          </Pressable>
        ))}
      </div>
    </div>
  );
}

// ── Step 5: Term ────────────────────────────────────────────────────────────

function TermStep({ term, onSelect, onSkip, onBack, loading }: {
  term: string | null;
  onSelect: (t: string) => void;
  onSkip: () => void;
  onBack: () => void;
  loading: boolean;
}) {
  return (
    <div className="space-y-6">
      <BackButton onClick={onBack} />
      <StepHeading title="What term?">
        This is optional.
      </StepHeading>
      <div className="grid grid-cols-2 gap-3">
        {TERMS.map(t => (
          <Pressable
            key={t}
            haptic="selection"
            onClick={() => onSelect(t)}
            disabled={loading}
            aria-pressed={term === t}
            className={`py-5 min-h-[56px] rounded-2xl text-base font-semibold border-2 transition-colors disabled:opacity-50 ${
              term === t
                ? 'border-squishy-teal bg-teal-50 text-squishy-tealDark shadow-glow'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 shadow-card'
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              {term === t && (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {t}
            </span>
          </Pressable>
        ))}
      </div>
      <Pressable
        onClick={onSkip}
        disabled={loading}
        className="w-full py-3.5 border-2 border-dashed border-slate-300 rounded-2xl text-sm text-slate-500 font-medium hover:border-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
      >
        {loading ? 'Setting up...' : 'Skip this step'}
      </Pressable>
    </div>
  );
}

// ── Step 6: Done ────────────────────────────────────────────────────────────

function DoneStep({ sectionId, classCode }: { sectionId: string; classCode: string }) {
  const nav = useNavigate();
  // Get question count for this section via the feed endpoint
  const { data: feedData, isLoading } = useFeedCount(sectionId);
  const questionCount = feedData?.count ?? 0;

  useEffect(() => { haptic.success(); }, []);

  return (
    <div className="space-y-8 text-center">
      <div className="fixed inset-0 pointer-events-none z-10" aria-hidden>
        <Confetti count={36} duration={1.8} />
      </div>

      <motion.div
        initial={{ scale: 0.4, opacity: 0, rotate: -12 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ ...springs.bouncy, delay: 0.1 }}
        className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-squishy-teal to-squishy-tealDark shadow-pop flex items-center justify-center relative"
      >
        <motion.span
          className="absolute inset-0 rounded-full border-2 border-squishy-teal"
          initial={{ scale: 1, opacity: 0.6 }}
          animate={{ scale: 1.45, opacity: 0 }}
          transition={{ delay: 0.35, duration: 0.9, ease: 'easeOut' }}
          aria-hidden
        />
        <svg viewBox="0 0 24 24" className="w-11 h-11" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <motion.path
            d="M5 13l4 4L19 7"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.35, duration: 0.4, ease: 'easeOut' }}
          />
        </svg>
      </motion.div>

      <FadeIn delay={0.25}>
        <h1 className="font-display text-3xl text-squishy-navy">You're all set!</h1>
        <p className="text-slate-500 mt-2">
          <span className="font-semibold text-squishy-navy">{classCode}</span> is officially on your list. Time to make it count.
        </p>
      </FadeIn>

      <FadeIn delay={0.4} className="space-y-3 pt-2">
        <Pressable
          haptic="medium"
          onClick={() => nav(`/class/${sectionId}`)}
          className="w-full bg-gradient-to-br from-squishy-tealDark to-teal-600 text-white py-4 rounded-2xl font-bold text-lg shadow-pop text-center"
        >
          Start Studying
          {!isLoading && questionCount > 0 && (
            <span className="block text-sm font-normal opacity-80 mt-0.5">
              {questionCount} question{questionCount !== 1 ? 's' : ''} ready
            </span>
          )}
          {!isLoading && questionCount === 0 && (
            <span className="block text-sm font-normal opacity-80 mt-0.5">
              Add questions first, or jump in
            </span>
          )}
        </Pressable>

        <Pressable
          onClick={() => nav('/upload')}
          className="w-full border-2 border-slate-200 bg-white text-slate-700 py-4 rounded-2xl font-semibold text-base hover:border-slate-300 transition-colors text-center"
        >
          Add Questions
          <span className="block text-sm font-normal text-slate-500 mt-0.5">
            Upload notes, slides, or paste text
          </span>
        </Pressable>
      </FadeIn>
    </div>
  );
}

// Quick hook to get question count for a class section
function useFeedCount(sectionId: string) {
  const { data, isLoading } = useQuestionsFeed({ class_section_id: sectionId });
  const count = (data?.questions ?? []).filter((q: any) => q.type !== 'flashcard' && q.type !== 'quick_mcq').length;
  return { data: { count }, isLoading };
}
